import {makeAutoObservable} from "mobx";
import {DayWeekObservable} from "./dayWeekObservable";
import {DayWeekState} from "../../shared/enums/eventDayWeekState";
import {forEach, isEqual} from "lodash";
import {utcToZonedTime} from "date-fns-tz";
import i18n from "i18next";
import {recurrenceWeekdays} from "../../shared/constants";

export class DayObservableList {
  weekdayList: DayWeekObservable[];
  // Used for tracking changes to days
  originalDays: number[] = [];

  constructor(
    weekdays?: number[],
    eventStartDate?: Date,
    timezoneCode?: string
  ) {
    makeAutoObservable(this);

    this.originalDays = weekdays ?? [];
    // Initialize weekList with default values
    this.weekdayList = [
      new DayWeekObservable(
        1,
        DayWeekState.selected,
        "event:labels:days:mon:label",
        "event:labels:days:mon:name"
      ),
      new DayWeekObservable(
        2,
        DayWeekState.selected,
        "event:labels:days:tue:label",
        "event:labels:days:tue:name"
      ),
      new DayWeekObservable(
        3,
        DayWeekState.selected,
        "event:labels:days:wed:label",
        "event:labels:days:wed:name"
      ),
      new DayWeekObservable(
        4,
        DayWeekState.selected,
        "event:labels:days:thu:label",
        "event:labels:days:thu:name"
      ),
      new DayWeekObservable(
        5,
        DayWeekState.selected,
        "event:labels:days:fri:label",
        "event:labels:days:fri:name"
      ),
      new DayWeekObservable(
        6,
        DayWeekState.selected,
        "event:labels:days:sat:label",
        "event:labels:days:sat:name"
      ),
      new DayWeekObservable(
        0,
        DayWeekState.selected,
        "event:labels:days:sun:label",
        "event:labels:days:sun:name"
      )
    ];

    if (weekdays && eventStartDate && timezoneCode) {
      let eventBaseDayOfWeek = utcToZonedTime(
        eventStartDate!,
        timezoneCode!
      ).getDay();
      forEach(this.weekdayList, (listWeekDay) => {
        let isSelectedDay = weekdays.includes(listWeekDay.index);
        if (isSelectedDay) {
          let dayIsEventBaseDay = eventBaseDayOfWeek === listWeekDay.index;
          listWeekDay.state = dayIsEventBaseDay
            ? DayWeekState.autoSelected
            : DayWeekState.selected;
        } else {
          listWeekDay.state = DayWeekState.default;
        }
      });
      this.enforceMinimumSelectedDays();
    }
  }

  resetSelections() {
    this.weekdayList.forEach((value) => (value.state = DayWeekState.selected));
  }

  enforceMinimumSelectedDays() {
    const selectedWeekNumbers = this.getSelectedDays();
    if (selectedWeekNumbers.length === 2) {
      this.setWeekDayState(DayWeekState.selected, DayWeekState.forceSelected);
    } else {
      this.setWeekDayState(DayWeekState.forceSelected, DayWeekState.selected);
    }
  }

  setWeekDayState(currentState: DayWeekState, newState: DayWeekState) {
    forEach(this.weekdayList, (value) => {
      if (value.state === currentState) {
        value.state = newState;
      }
    });
  }

  getDisplayLabel() {
    const selectedDays = this.getSelectedDays().map(
      (value) => `$t(${value.fullLabel})`
    );
    let displayLabel = "$t(event:labels:days:day)";
    if (selectedDays.length !== 7) {
      const lastSelected = selectedDays.pop();
      displayLabel = i18n.t("event:messages.recurrenceSummaryDayWeekMultiple", {
        firstValues: selectedDays.join(", "),
        lastValue: lastSelected
      });
    }
    return displayLabel;
  }

  getSelectedDays() {
    return this.weekdayList.filter(
      (value) =>
        value.state === DayWeekState.selected ||
        value.state === DayWeekState.autoSelected ||
        value.state === DayWeekState.forceSelected
    );
  }

  getSelectedDaysByIndex(): number[] {
    const days = this.getSelectedDays().map((value) => value.index);
    return days;
  }

  getSelectedDaysByInitial(): string[] {
    const days = this.getSelectedDays().map(
      (value) => recurrenceWeekdays[value.index]
    );
    return days;
  }

  getDay(index: number): DayWeekObservable | undefined {
    return this.weekdayList.find((value) => value.index === index);
  }

  hasChange() {
    return !isEqual(
      this.originalDays.sort(),
      this.getSelectedDaysByIndex().sort()
    );
  }
}
